// "Save to program documents" — the workroom's artifact → program_documents
// bridge. Renders only inside a WorkroomProgramProvider (the drawer is shared
// with /assistant and project chats, where there is no program to save to).
//
// Flow: artifact content → File → tesoro upload session (browser presigned
// PUT, same path as every other upload) → attach to the backing zou project
// (so it also lists under project files) → CreateProgramDocument with the
// minted artifact id. The description carries the AI-drafted label — per the
// blueprint §16 risk control, generated artifacts stay marked until a human
// reviews them.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FolderInput } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { useWorkroomProgram } from '../../contexts/WorkroomProgramContext';
import { projectApiConnect } from '../../lib/api/projectApi.connect';
import { uploadAttachmentViaTesoro } from '../../lib/uploadAttachment';
import { useCreateProgramDocument } from '../../pages/Delivery/Docs/hooks';
// Same closed set the Docs "Record Document" form uses.
const CATEGORY_OPTIONS = [
    'PMR',
    'TPP',
    'Decision Log',
    'Kickoff',
    'Contract',
    'Subcontract',
    'Other'
];
const AI_DRAFTED_DESCRIPTION = 'AI-drafted in the program workroom (unreviewed).';
// Extension + MIME by artifact kind; artifact.contentType wins when present.
// image is excluded entirely — its content isn't a text payload.
const KIND_FILE = {
    text: {
        ext: 'md',
        mime: 'text/markdown'
    },
    code: {
        ext: 'txt',
        mime: 'text/plain'
    },
    sheet: {
        ext: 'csv',
        mime: 'text/csv'
    },
    presentation: {
        ext: 'html',
        mime: 'text/html'
    }
};
// Outer gate: the drawer is shared with /assistant and project chats, which
// render without AuthProvider in tests and without a workroom everywhere.
// Only the inner component touches auth/query hooks, so outside the Workroom
// this renders null without executing any of them.
export function SaveToProgramDocsButton({ artifact }) {
    const workroom = useWorkroomProgram();
    const kindFile = artifact ? KIND_FILE[artifact.kind] : undefined;
    if (!workroom || !artifact || !kindFile) return null;
    return /*#__PURE__*/ _jsx(SaveToProgramDocsButtonInner, {
        artifact: artifact,
        kindFile: kindFile,
        workroom: workroom
    });
}
function SaveToProgramDocsButtonInner({ artifact, kindFile, workroom }) {
    const { user } = useAuth();
    const createDoc = useCreateProgramDocument();
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('Other');
    const [saving, setSaving] = useState(false);
    if (!user?.tenant_id) return null;
    // Narrowed copy — the async closure below can't rely on the guard above.
    const tenantId = user.tenant_id;
    const handleOpen = ()=>{
        setTitle(artifact.title);
        setOpen(true);
    };
    const handleSave = async ()=>{
        if (!title.trim()) {
            toast.error('Title is required');
            return;
        }
        setSaving(true);
        try {
            const fileName = `${title.trim()}.${kindFile.ext}`;
            const file = new File([
                artifact.content ?? ''
            ], fileName, {
                type: artifact.contentType || kindFile.mime
            });
            const uploaded = await uploadAttachmentViaTesoro(file, {
                tenantId,
                ownerUserId: user.id,
                projectId: workroom.zouProjectId,
                autoProcess: false
            });
            // Also list it under the backing project's files, mirroring the
            // regular upload path (viewer, delete, lineage all keep working).
            await projectApiConnect.attachArtifactsToProject(workroom.zouProjectId, [
                uploaded.artifactId
            ]);
            await createDoc.mutateAsync({
                programId: workroom.programId,
                title: title.trim(),
                description: AI_DRAFTED_DESCRIPTION,
                docDate: new Date(),
                fileId: uploaded.artifactId,
                fileName,
                category,
                uploadedBy: user.name || user.email || ''
            });
            setOpen(false);
        } catch (err) {
            // createDoc's onError already toasts its own failures; this covers the
            // upload/attach legs.
            if (!createDoc.isError) {
                toast.error(err instanceof Error ? err.message : 'Failed to save to program');
            }
        } finally{
            setSaving(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("button", {
                title: "Save to program documents",
                className: "p-1 rounded hover:bg-muted flex items-center justify-center w-6 h-6",
                type: "button",
                onClick: handleOpen,
                "aria-label": "Save to program documents",
                "data-testid": Testids.workroom.saveToProgram,
                children: /*#__PURE__*/ _jsx(FolderInput, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(Dialog, {
                open: open,
                onOpenChange: setOpen,
                children: /*#__PURE__*/ _jsxs(DialogContent, {
                    "data-testid": Testids.workroom.saveDialog,
                    children: [
                        /*#__PURE__*/ _jsx(DialogHeader, {
                            children: /*#__PURE__*/ _jsx(DialogTitle, {
                                children: "Save to program documents"
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "workroom-save-title",
                                            children: "Title"
                                        }),
                                        /*#__PURE__*/ _jsx(Input, {
                                            id: "workroom-save-title",
                                            value: title,
                                            onChange: (e)=>setTitle(e.target.value),
                                            maxLength: 300
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Category"
                                        }),
                                        /*#__PURE__*/ _jsxs(Select, {
                                            value: category,
                                            onValueChange: setCategory,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: CATEGORY_OPTIONS.map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: c,
                                                            children: c
                                                        }, c))
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: [
                                        "Saved as",
                                        ' ',
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-mono",
                                            children: `${title.trim() || 'untitled'}.${kindFile.ext}`
                                        }),
                                        ' ',
                                        "and labeled AI-drafted until reviewed."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    onClick: ()=>setOpen(false),
                                    disabled: saving,
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    onClick: handleSave,
                                    disabled: saving,
                                    "data-testid": Testids.workroom.saveSubmit,
                                    children: saving ? 'Saving…' : 'Save to program'
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
